import { useEffect, useMemo, useState } from "react";

import { PositionsData } from "./types";

const REMOVED_POSITIONS_CHANGED_EVENT = "removedPositionsChanged";

export function getRemovedPositionsStorageKey(chainId: number, account: string) {
  return `removed-positions:${chainId}:${account.toLowerCase()}`;
}

export function readRemovedPositionKeys(storageKey: string | undefined) {
  if (!storageKey || typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === "string") : [];
  } catch (error) {
    void error;
    return [];
  }
}

export function writeRemovedPositionKeys(storageKey: string, keys: string[]) {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(storageKey, JSON.stringify(keys));
  window.dispatchEvent(new CustomEvent(REMOVED_POSITIONS_CHANGED_EVENT, { detail: { storageKey } }));
}

export function addRemovedPositionKey(chainId: number, account: string, positionKey: string) {
  const storageKey = getRemovedPositionsStorageKey(chainId, account);
  const keys = new Set(readRemovedPositionKeys(storageKey));
  keys.add(positionKey);
  writeRemovedPositionKeys(storageKey, Array.from(keys));
}

export function useRemovedPositionKeys(chainId: number, account: string | null | undefined) {
  const storageKey = useMemo(() => (account ? getRemovedPositionsStorageKey(chainId, account) : undefined), [
    account,
    chainId,
  ]);
  const [removedPositionKeys, setRemovedPositionKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const syncKeys = () => {
      setRemovedPositionKeys(new Set(readRemovedPositionKeys(storageKey)));
    };

    syncKeys();

    if (typeof window === "undefined") return undefined;

    window.addEventListener(REMOVED_POSITIONS_CHANGED_EVENT, syncKeys);
    window.addEventListener("storage", syncKeys);

    return () => {
      window.removeEventListener(REMOVED_POSITIONS_CHANGED_EVENT, syncKeys);
      window.removeEventListener("storage", syncKeys);
    };
  }, [storageKey]);

  return removedPositionKeys;
}

export function filterRemovedPositionsData(
  positionsData: PositionsData | undefined,
  removedPositionKeys: Set<string>
): PositionsData | undefined {
  if (!positionsData || removedPositionKeys.size === 0) return positionsData;

  const filtered = Object.values(positionsData).reduce((acc, position) => {
    if (!removedPositionKeys.has(position.key)) {
      acc[position.key] = position;
    }

    return acc;
  }, {} as PositionsData);

  return filtered;
}
